import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Users, Shield, TrendingUp, Handshake, Phone, Mail } from "lucide-react";
import { SITE_INFO } from "@/data/siteData";

const VANTAGGI = [
  { icon: Shield, titolo: "Rappresentanza istituzionale", desc: "La tua voce presso Comune, Regione e Governo. Portiamo le istanze delle imprese ai tavoli che contano." },
  { icon: Handshake, titolo: "Relazioni e networking", desc: "Entra in una rete di migliaia di imprenditori. Eventi, incontri, progetti condivisi per crescere insieme." },
  { icon: TrendingUp, titolo: "Servizi e consulenza", desc: "Consulenza legale, fiscale, sul lavoro. Check-up gratuiti, assistenza HACCP, privacy, sicurezza." },
  { icon: Users, titolo: "Formazione continua", desc: "Corsi obbligatori e di crescita professionale, finanziati e a distanza. Per te e i tuoi dipendenti." },
];

const TESTIMONIAL = [
  { nome: "Marco B.", ruolo: "Ristoratore, FIPE Roma", testo: "Grazie a Confcommercio Roma ho avuto accesso a bandi che non avrei mai trovato da solo. Il supporto è concreto e tempestivo." },
  { nome: "Giulia T.", ruolo: "Agente immobiliare, FIMAA Roma", testo: "La formazione e il networking sono il vero valore aggiunto. Ho costruito relazioni professionali che hanno fatto crescere la mia attività." },
  { nome: "Alessandro R.", ruolo: "Freelance, Professioni Roma", testo: "Come partita IVA mi sentivo isolato. Con Confcommercio ho trovato una comunità e servizi su misura per le mie esigenze." },
];

export default function DiventaSocioPage() {
  return (
    <main id="main-content" data-testid="diventa-socio-page">
      {/* Hero */}
      <section className="relative bg-[var(--confcom-blue)] overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white rounded-full -translate-y-1/2 translate-x-1/3" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] mb-4 font-['Encode_Sans_Semi_Condensed']">Entra nella comunità</span>
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-[1.1] mb-6">
              Diventa socio di Confcommercio Roma
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Unisciti a migliaia di imprese che ogni giorno trovano in Confcommercio Roma opportunità, relazioni e supporto concreto per il proprio business.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contatto" className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity shadow-lg text-sm" data-testid="cta-diventa-socio">
                Richiedi informazioni <ArrowRight size={18} aria-hidden="true" />
              </a>
              <a href={`tel:${SITE_INFO.greenNumber.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm">
                <Phone size={18} aria-hidden="true" /> {SITE_INFO.greenNumber}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Vantaggi */}
      <section className="py-20 bg-white" aria-labelledby="vantaggi-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Perché associarsi</span>
            <h2 id="vantaggi-title" className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Cosa significa far parte della comunità
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {VANTAGGI.map((v) => (
              <div key={v.titolo} className="flex gap-5 p-6 rounded-xl border border-[var(--confcom-border)]" data-testid={`vantaggio-${v.titolo.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="w-12 h-12 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center shrink-0">
                  <v.icon size={24} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{v.titolo}</h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-[var(--confcom-bg-subtle)]" aria-labelledby="testimonial-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="testimonial-title" className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-10 text-center">La parola ai nostri associati</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIAL.map((t) => (
              <div key={t.nome} className="bg-white p-6 rounded-xl border border-[var(--confcom-border)]" data-testid={`testimonial-${t.nome.toLowerCase().replace(/\s/g, "-")}`}>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed italic mb-5">"{t.testo}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-[var(--confcom-border)]">
                  <div className="w-10 h-10 bg-[var(--confcom-blue)] rounded-full flex items-center justify-center text-white font-bold text-sm font-['Encode_Sans_Semi_Condensed']">
                    {t.nome.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">{t.nome}</div>
                    <div className="text-xs text-[var(--confcom-text-muted)]">{t.ruolo}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contatto */}
      <section id="contatto" className="py-20 bg-[var(--confcom-blue)]" aria-labelledby="contatto-title">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="contatto-title" className="text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-4">Inizia il percorso</h2>
          <p className="text-lg text-white/80 mb-8">Contattaci e scopri come Confcommercio Roma può supportare la tua impresa.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="mailto:associati@confcommercioroma.it" className="inline-flex items-center justify-center gap-2 bg-white text-[var(--confcom-blue)] px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity" data-testid="cta-email-socio">
              <Mail size={18} aria-hidden="true" /> associati@confcommercioroma.it
            </a>
            <a href="tel:0668437271" className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity" data-testid="cta-tel-socio">
              <Phone size={18} aria-hidden="true" /> 06.68437271
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
